import { useEffect, useState } from 'react';
import { Input, Label, Message, Radio, Slider } from '@iwoca/orion';
import { Trans, t } from '@lingui/macro';
import clsx from 'clsx';
import { addMonths, isBefore, isAfter } from 'date-fns';
import { TDurationDetails } from 'types';
import { dateToString } from 'utils/common/common';
import { calculateMonthDifference } from 'utils/durationHelper';
import InputFormatter from 'utils/inputFormatters/inputFormatters';
import { MIN_DURATION } from '../filterConfigs.helper';
import standAloneCalculatorStyles from '../StandAloneCalculator.module.css';
import styles from './DurationFilter.module.css';

type TProps = {
  maxTermLength: number;
  isDisabled: boolean;
  onChange: (duration: TDurationDetails) => void;
};

export const DurationFilter = ({
  maxTermLength,
  isDisabled,
  onChange,
}: TProps) => {
  const halfMaxTerm = (maxTermLength / 2).toString();
  const [duration, setDuration] = useState(halfMaxTerm);
  const [isCustomDate, setIsCustomDate] = useState(false);
  const [customDuration, setCustomDuration] = useState<string>('0');
  const [customStartDate, setCustomStartDate] = useState('');
  const [customEndDate, setCustomEndDate] = useState('');
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    setCustomDuration(
      (
        calculateMonthDifference(
          new Date(customStartDate),
          new Date(customEndDate),
        ) + 1
      ).toString(),
    );

    if (!customStartDate && customEndDate) {
      setCustomStartDate(dateToString(new Date()));
    }
  }, [customStartDate, customEndDate]);

  useEffect(() => {
    if (!isDisabled) {
      const presetDuration = isCustomDate ? customDuration : duration;

      const presetEndDate = isCustomDate
        ? customEndDate
        : dateToString(addMonths(new Date(), Number(duration)));

      const data = {
        startDate: isCustomDate ? customStartDate : dateToString(new Date()),
        endDate: Number(presetDuration) >= maxTermLength ? null : presetEndDate,
        duration: presetDuration,
      };
      onChange(data);
    }

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    duration,
    customDuration,
    customStartDate,
    customEndDate,
    isCustomDate,
    isDisabled,
  ]);

  const resetInput = () => {
    setDuration(halfMaxTerm);
    setIsCustomDate(false);
    setCustomDuration('0');
    setCustomStartDate('');
    setCustomEndDate('');
  };

  useEffect(() => {
    resetInput();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [maxTermLength]);

  const minimumEndDate = customStartDate || dateToString(new Date());

  const maximumEndDate = dateToString(
    addMonths(new Date(minimumEndDate), maxTermLength),
  );

  const handleBlur = (newValue: string) => {
    setIsCustomDate(false);
    setError(null);

    if (Number(newValue) > maxTermLength) {
      setDuration(maxTermLength.toString());
    } else if (Number(newValue) < Number(MIN_DURATION)) {
      setDuration(MIN_DURATION);
    }
  };

  const handleStartDateChange = (startDate: string) => {
    setIsCustomDate(true);
    setCustomStartDate(startDate);
  };

  const handleEndDateChange = (endDate: string) => {
    setIsCustomDate(true);
    setCustomEndDate(endDate);
  };

  const handleStartDateBlur = (startDate: string) => {
    if (isBefore(new Date(startDate), new Date())) {
      return setCustomStartDate(dateToString(new Date()));
    }

    setCustomStartDate(startDate);
  };

  const handleEndDateBlur = (endDate: string) => {
    if (isBefore(new Date(endDate), new Date())) {
      return setCustomEndDate(dateToString(new Date()));
    }

    if (isAfter(new Date(endDate), new Date(maximumEndDate))) {
      return setCustomEndDate(dateToString(new Date(maximumEndDate)));
    }

    setCustomEndDate(endDate);
  };

  const handleIntegerChange = (value: string) => {
    setError(null);
    if (Number(value) > maxTermLength) {
      setError(t`Please enter a maximum of ${maxTermLength}`);
    } else if (Number(value) < Number(MIN_DURATION)) {
      setError(t`Please enter a minimum of ${MIN_DURATION}`);
    }

    setDuration(InputFormatter.Integer(value));
  };

  return (
    <div className={styles.container}>
      <div className={styles.fixedDurationContainer}>
        <div className={styles.inputContainer}>
          <Radio
            name="isCustomDuration"
            className={styles.radioButton}
            onChange={() => setIsCustomDate(false)}
            value="false"
            checked={!isCustomDate}
          />
          <Label
            htmlFor="duration"
            className={clsx(styles.label, {
              [styles.unselectedLabel]: isCustomDate,
            })}
          >
            <Trans>Duration</Trans>
          </Label>
          <Input
            id="duration"
            className={styles.input}
            value={duration}
            onChange={(e) => {
              setIsCustomDate(false);
              handleIntegerChange(e.target.value);
            }}
            onBlur={(e) => handleBlur(e.target.value)}
          />
          <span className={styles.subText}>
            {duration === '1' ? <Trans>Month</Trans> : <Trans>Months</Trans>}
          </span>
        </div>
        <Slider
          data-testid="duration-slider"
          value={duration}
          step={1}
          labelClassName={standAloneCalculatorStyles.label}
          leftLabel={t`1 month`}
          rightLabel={t`${maxTermLength} months`}
          min={Number(MIN_DURATION)}
          max={maxTermLength}
          onChange={(e) => {
            setIsCustomDate(false);
            setDuration(e.target.value);
          }}
        />
        {error && (
          <Message
            className={styles.error}
            size="small"
            type="error"
            message={error}
          />
        )}
      </div>
      <div className={styles.customDurationContainer}>
        <Radio
          className={styles.radioButton}
          onChange={() => setIsCustomDate(true)}
          name="isCustomDuration"
          value="true"
          checked={isCustomDate}
        />
        <Label
          htmlFor="custom-duration-start"
          className={clsx(styles.label, {
            [styles.unselectedLabel]: !isCustomDate,
          })}
        >
          <Trans>From</Trans>
        </Label>
        <Input
          type="date"
          id="custom-duration-start"
          data-testid="custom-duration-start"
          className={styles.customInput}
          value={customStartDate}
          onChange={(e) => handleStartDateChange(e.target.value)}
          onBlur={(e) => handleStartDateBlur(e.target.value)}
          min={dateToString(new Date())}
        />
        <span className={styles.subText}>
          <Trans>to</Trans>
        </span>
        <Input
          type="date"
          id="custom-duration-end"
          data-testid="custom-duration-end"
          className={styles.customInput}
          value={customEndDate}
          onChange={(e) => handleEndDateChange(e.target.value)}
          onBlur={(e) => handleEndDateBlur(e.target.value)}
          min={minimumEndDate}
          max={maximumEndDate}
        />
      </div>
    </div>
  );
};
